import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  usersForCurrentRoleState,
  User,
  usersState,
  showUsersRoleModalState,
  currentRoleState,
  Role,
} from 'states';
import { useRolesService, useUserService } from 'services';

import { Avatar, BodyTwo, ModalOne, Notify, ListCardTwo } from 'components/core';
import { initialGenerator } from 'helpers';
import { isEqual } from 'lodash';
import { ConfirmDialog } from 'molecules/confirmDialog/ConfirmDialog';
// Not a React component/hook, so the caller (inside Users) passes its own
// `t` from useTranslation() rather than this calling the hook itself.
const handleSubmit =
  (
    roleUsers: User[],
    usersForCurrentRole: User[],
    currentRole: any,
    roleService: any,
    setIsSaving: (val: boolean) => void,
    setShowAddUserModal: (val: boolean) => void,
    setUsersForCurrentRole: (val: User[]) => void,
    t: TFunction
  ) =>
  (e: any) => {
    e.preventDefault();
    if (isEqual(roleUsers, usersForCurrentRole)) {
      setShowAddUserModal(false);
      return false;
    }
    setIsSaving(true);
    const roleUsersMap = {
      userIds: roleUsers.map((user) => user.id),
      roleId: currentRole.id,
    };
    roleService.usersRoleMapping(roleUsersMap).then(
      () => {
        setIsSaving(false);
        setShowAddUserModal(false);
        setUsersForCurrentRole(roleUsers);
        Notify.success(t('ROLE.USERS_MAPPED_SUCCESS'));
      },
      (error: any) => {
        setIsSaving(false);
        Notify.error(error);
        setShowAddUserModal(false);
      }
    );
  };

const handleCancel =
  (
    isSaving: boolean,
    usersForCurrentRole: User[],
    setShowAddUserModal: (val: boolean) => void,
    setRoleUsers: (val: User[]) => void,
    setIsSaving: (val: boolean) => void
  ) =>
  (e: any) => {
    e.preventDefault();
    if (isSaving) return false;
    setShowAddUserModal(false);
    setRoleUsers(usersForCurrentRole);
    setIsSaving(false);
  };

export const Users = () => {
  const { t } = useTranslation();
  const [roleUsers, setRoleUsers] = useState<User[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useRecoilState(showUsersRoleModalState);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersForCurrentRole, setUsersForCurrentRole] = useRecoilState(usersForCurrentRoleState);
  const currentRole = useRecoilValue(currentRoleState);
  const users = useRecoilValue(usersState);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const roleService = useRolesService();
  const userService = useUserService();

  useEffect(() => {
    if (currentRole?.id && users) {
      const filteredUsers = users.filter((user: User) =>
        user.roles?.some((role: Role) => role.id === currentRole.id)
      );

      setUsersForCurrentRole(filteredUsers);
    }
  }, [users, currentRole.id]);

  useEffect(() => {
    setRoleUsers(usersForCurrentRole);
  }, [usersForCurrentRole]);

  useEffect(() => {
    userService.getUsers(false).then((response) => {
      if (response && response.length > 0) {
        setAllUsers(response);
      }
    });
  }, [users]);

  const updateUsers = (e: any, selectedUser: User) => {
    e.preventDefault();
    const currentRoleUsers: User[] = [...roleUsers];
    const selectedUserIndex = currentRoleUsers.findIndex((user) => user.id === selectedUser.id);
    if (selectedUserIndex === -1) {
      currentRoleUsers.push(selectedUser);
    } else {
      currentRoleUsers.splice(selectedUserIndex, 1);
    }
    setRoleUsers(currentRoleUsers);
  };
  const getUserClickHandler = (user: any) => {
    return (e: any) => {
      updateUsers(e, user);
    };
  };

  const renderModalDataLeft = () => {
    if (allUsers && allUsers.length > 0) {
      return (
        <>
          {allUsers.map((user: User) => {
            return (
              <div className="mb-3" key={user.id}>
                <ListCardTwo
                  name={user.firstName! || user.emailId}
                  initialText={initialGenerator(user.firstName!)}
                  showAvatar={true}
                  enableCheck={true}
                  clickHandler={getUserClickHandler(user)}
                />
              </div>
            );
          })}
        </>
      );
    }
  };

  const save = handleSubmit(
    roleUsers,
    usersForCurrentRole,
    currentRole,
    roleService,
    setIsSaving,
    setShowAddUserModal,
    setUsersForCurrentRole,
    t
  );
  // Saving a list that leaves people out takes this role's access away from them, so it asks first.
  const removedCount = usersForCurrentRole.filter((u: User) => !roleUsers.some((r) => r.id === u.id)).length;
  const saveOrAsk = (e: any) => {
    if (removedCount > 0) {
      e.preventDefault();
      setConfirmingRemoval(true);
      return false;
    }
    return save(e);
  };
  const confirmSave = (e: any) => {
    setConfirmingRemoval(false);
    save(e);
  };

  return (
    <>
      <div className="col-12 mt-4 mb-3">
        <ModalOne
          modalLabel="modalLabelAddUsers"
          modalTitle={t('ROLE.ADD_USERS')}
          btnTextPrimary={t('ACTION.SAVE')}
          primaryBtnHandler={saveOrAsk}
          secondaryBtnHandler={handleCancel(
            isSaving,
            usersForCurrentRole,
            setShowAddUserModal,
            setRoleUsers,
            setIsSaving
          )}
          renderLeft={renderModalDataLeft()}
          isLarge={false}
          showModal={showAddUserModal}
          processingPrimaryBtnHandler={isSaving}
        />
        <ConfirmDialog
          show={confirmingRemoval}
          title={t('CONFIRM_REMOVE.ROLE_USERS_TITLE')}
          message={t('CONFIRM_REMOVE.ROLE_USERS_MESSAGE', { count: removedCount })}
          confirmLabel={t('CONFIRM_REMOVE.REMOVE')}
          onConfirm={confirmSave}
          onCancel={() => setConfirmingRemoval(false)}
        />
        <div className="mt-4 px-1">
          {/* User row */}
          {Object.keys(usersForCurrentRole).length > 0 &&
            usersForCurrentRole.map((user: User) => {
              return (
                <div className="" key={user.id}>
                  <div className="row mb-3">
                    <div className="col-sm-12 col-md-6 col-lg-6">
                      <div className="row p-0 m-0">
                        <div className="col-1 p-0">
                          <Avatar
                            initials={initialGenerator(user.firstName!)}
                            variant="small"
                          ></Avatar>
                        </div>
                        <div className="col-7 pt-2 mx-3 mx-md-4">
                          <BodyTwo content={user.firstName || user.emailId} />
                        </div>
                      </div>
                    </div>
                    {/* Uncomment below section when association date available */}
                    {/* <div className="col-sm-12 col-md-6 col-lg-6 pt-1">
                      <div className="float-end col-12">
                        <div className="row p-0 m-0">
                          <div className="pt-2 col-sm-12 col-md-12 col-lg-12">
                            <div className="float-end">
                              <Caption
                                // text={`added on ${moment
                                //   .unix(k.addedOn)
                                //   .format("DD/MM/YYYY")}`}
                                text={`added on DD/MM/YYYY`}
                              ></Caption>
                            </div>
                          </div>
                        </div>
                      </div>
                    
                    
                    </div> */}
                  </div>
                  <hr />
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};
